import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService, UserProfile, addressBookService, AddressBookData } from '../services';

interface DeliveryAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  pincode: string;
  geoLocation?: {
    type: string;
    coordinates: [number, number];
  };
}

interface ProfileContextType {
  profile: UserProfile | null;
  addresses: AddressBookData[];
  isLoading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  fetchAddresses: () => Promise<void>;
  getDeliveryAddress: () => DeliveryAddress;
}

export const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [addresses, setAddresses] = useState<AddressBookData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user profile from API
  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response: any = await authService.getUserProfile();
      if (response && response.data) {
        setProfile(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch profile:', error);
      setError(error?.message || 'Failed to fetch profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
    try {
      setIsLoading(true);
      setError(null);
      const response: any = await authService.updateProfile(data);
      if (response && response.data) {
        setProfile(response.data);
      }
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setError(error?.message || 'Failed to update profile');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch addresses from address book
  const fetchAddresses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response: any = await addressBookService.getAllAddresses();
      if (response && response.data) {
        setAddresses(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch addresses:', error);
      setError(error?.message || 'Failed to fetch addresses');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get delivery address from address book
  const getDeliveryAddress = useCallback((): DeliveryAddress => {
    // Find the default address from address book
    const defaultAddress = addresses.find(addr => addr.isDefault);
    
    // Use default address if available, otherwise use first address
    const selectedAddress = defaultAddress || addresses[0];

    if (!selectedAddress) {
      // Fallback to empty address if no addresses in address book
      return {} as DeliveryAddress;
    }

    // Return address from address book
    return {
      fullName: selectedAddress.fullName,
      phone: selectedAddress.phone,
      addressLine1: selectedAddress.addressLine1,
      addressLine2: selectedAddress.addressLine2 || "",
      city: selectedAddress.city,
      pincode: selectedAddress.pincode,
      geoLocation: selectedAddress.geoLocation || {
        type: "Point",
        coordinates: [0, 0]
      }
    };
  }, [addresses]);

  // Fetch profile and addresses on mount if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchProfile();
      fetchAddresses();
    }
  }, [fetchProfile, fetchAddresses]);

  const value: ProfileContextType = {
    profile,
    addresses,
    isLoading,
    error,
    fetchProfile,
    updateProfile,
    fetchAddresses,
    getDeliveryAddress,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
